/**
 * LakshmiDwadashamshaCalculator - Lazy calculator for D12 Lakshmi Dwadashamsha
 * Provides async calculation of 12 Lakshmi qualities using Surya as reference
 */

import { devLog, devWarn } from '../../../../../utils/devLog';
import { JatakaResponse } from '../../../../../models/api';
import { lazyCalculator } from '../lazy/SimpleLazyCalculator';
import { LAKSHMI_DWADASHAMSHA_ATTRIBUTES } from '../data/LakshmiDwadashamshaTableData';
import {
    getSuryaLongitudeFromApi,
    parseLongitudeFromPosition,
    createPlanetDisplay,
    getCurrentHoraPlanet
} from '../utils/calculationUtils';

/**
 * D12 Lakshmi result interface
 */
export interface LakshmiDwadashamshaResult {
    planetsByQuality: Record<number, Array<{
        shortName: string;
        isRetrograde: boolean;
        isHora: boolean;
        isChandra: boolean;
        isLagna: boolean;
        displayText: string;
        globalLongitude: number;
        adjustedLongitude: number;
        lakshmiQuality: number;
    }>>;
    suryaReferenceLongitude: number;
    calculationTime: number;
    totalPlanets: number;
    qualitiesCount: number;
}

/**
 * Calculate D12 Lakshmi Dwadashamsha with Surya reference - optimized for many tabs
 */
async function calculateLakshmiDwadashamsha(
    apiResponse: JatakaResponse
): Promise<LakshmiDwadashamshaResult> {
    const startTime = Date.now();

    // Initialize result structure for 12 qualities
    const planetsByQuality: Record<number, any[]> = {};
    for (let i = 1; i <= 12; i++) {
        planetsByQuality[i] = [];
    }

    // Get Surya longitude as reference point (0°)
    const suryaLongitude = getSuryaLongitudeFromApi(apiResponse);

    // Get current hora planet for highlighting
    const horaPlanetName = getCurrentHoraPlanet(apiResponse);


    devLog(`🌟 D12 Lakshmi: Surya reference longitude: ${suryaLongitude.toFixed(2)}`);

    // Process each planet
    if (apiResponse.graha_positions?.planets) {
        const planets = apiResponse.graha_positions.planets;
        const _totalPlanets = planets.length;

        for (let i = 0; i < planets.length; i++) {
            const planet = planets[i];


            // Parse global longitude
            const globalLongitude = parseLongitudeFromPosition(planet.position);
            if (globalLongitude === null) {
                devWarn(`⚠️ LakshmiCalculator: Failed to parse longitude for ${planet.name}`);
                continue;
            }

            // Calculate adjusted longitude relative to Surya
            const adjustedLongitude = ((globalLongitude - suryaLongitude + 360) % 360);

            // Calculate Lakshmi quality (1-12) - degree within 12° determines quality
            const lakshmiQuality = Math.floor(adjustedLongitude / 30) + 1; // Each quality is 30° (360°/12)

            // Ensure valid range
            const finalQuality = Math.max(1, Math.min(12, lakshmiQuality));

            // Determine planet properties
            const isLagna = planet.short_name === 'Lg';
            const isChandra = planet.short_name === 'Ch';
            const isRetrograde = planet.is_retrograde || false;
            const isHora = !!(horaPlanetName && (
                planet.short_name === horaPlanetName ||
                planet.name === horaPlanetName
            ));

            // Create display text
            let displayText = planet.short_name;
            if (isRetrograde) {
                displayText = `(${planet.short_name})`;
            }

            const planetDisplay = {
                shortName: planet.short_name,
                isRetrograde,
                isHora,
                isChandra,
                isLagna,
                displayText,
                globalLongitude,
                adjustedLongitude,
                lakshmiQuality: finalQuality
            };

            // Add to appropriate quality
            planetsByQuality[finalQuality].push(planetDisplay);

            devLog(`📍 ${planet.name} (${planet.short_name}): global ${globalLongitude.toFixed(2)}° → adjusted ${adjustedLongitude.toFixed(2)}° → Lakshmi quality ${finalQuality}`);
        }
    }

    // Simulate processing time for realistic async behavior
    await new Promise(resolve => setTimeout(resolve, 30));

    const calculationTime = Date.now() - startTime;
    const totalPlanets = apiResponse.graha_positions?.planets?.length || 0;

    const result: LakshmiDwadashamshaResult = {
        planetsByQuality,
        suryaReferenceLongitude: suryaLongitude,
        totalPlanets,
        calculationTime,
        qualitiesCount: LAKSHMI_DWADASHAMSHA_ATTRIBUTES.length
    };

    devLog(`✅ LakshmiDwadashamshaCalculator: Completed in ${calculationTime}ms - processed ${totalPlanets} planets across ${result.qualitiesCount} qualities`);

    return result;
}

/**
 * Generate cache key for D12 Lakshmi calculation
 */
function generateCacheKey(apiResponse: JatakaResponse): string {
    if (!apiResponse.graha_positions?.planets) {
        return 'no-planets';
    }

    // Include Surya position as it's the reference point
    const suryaLongitude = getSuryaLongitudeFromApi(apiResponse);

    // Create cache key from all planet positions and hora
    const planetPositions = apiResponse.graha_positions.planets
        .map(p => `${p.short_name}:${p.position}:${p.is_retrograde ? 'R' : 'D'}`)
        .join('|');

    const horaInfo = apiResponse.detailed_panchanga?.hora_details?.current_planet || 'no-hora';

    return `surya:${suryaLongitude.toFixed(2)}|${planetPositions}|hora:${horaInfo}`;
}

// Register the lazy calculator - simplified for manual cache management
lazyCalculator.register({
    id: 'lakshmi-dwadashamsha-d12',
    calculator: calculateLakshmiDwadashamsha,
    cacheKey: generateCacheKey,
    ttl: 1200000 // 20 minutes - manual invalidation when positions change
});

export { calculateLakshmiDwadashamsha, generateCacheKey };